import { SnippetFolderOutlined as SnippetFolderOutlinedIcon } from "@mui/icons-material";
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { Typography } from "@platform/design-system";
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  useRfpDetails,
  useUpdateRfpBrokerage,
  useUpdateRfpClient,
  useUpdateRfpDetails,
} from "./api";
import {
  BEN_ADMIN_OPTIONS,
  type NotesTab,
  TECH_CREDIT_OPTIONS,
  US_STATES,
  YES_NO_OPTIONS,
} from "./constants";
import {
  analystOptionValue,
  assignedAnalystName,
  buildAnalystOptions,
  buildAssignedAnalystPayload,
  NO_ASSIGNED_ANALYST,
} from "./assignedAnalyst";
import {
  formatBrokerageAddress,
  formatCarrierDueDate,
  formatClientAddress,
  formatEffectiveDate,
  useReviewSectionState,
} from "./hooks/useReviewSectionState";
import MiniSection from "./MiniSection";
import Section from "./Section";
import { StepState } from "./stepMachine";

import type { SendRfpBrokerageDetails, SendRfpClientDetails } from "./api";
import type {
  SendRfpClientDetailsUpdate,
  CoverageNoteSection,
} from "./api/types";
import type { AnalystOption, AssignedAnalyst } from "./assignedAnalyst";
import type {
  EditableBrokerageFields,
  EditableClientFields,
  EditableRfpFields,
  EditedReviewFields,
} from "./hooks/useReviewSectionState";

const ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;

// SharePoint folder analysts must upload carrier documents to before submitting.
// The name is shown in the copy and is the deepest segment of the folder link.
const CARRIER_DOCS_FOLDER_NAME = "Final RFP Documents for Carriers";

/**
 * Builds the link to the RFP's "Final RFP Documents for Carriers" SharePoint
 * folder. analystDocsFolderUrl points at the analyst-documents root, so the
 * carrier-docs subpath is appended and each segment URL-encoded for the spaces.
 */
export const buildCarrierDocsFolderUrl = (
  analystDocsFolderUrl: string | undefined,
): string | undefined =>
  analystDocsFolderUrl
    ? `${analystDocsFolderUrl}/${encodeURIComponent(
        "RFP Documents",
      )}/${encodeURIComponent(CARRIER_DOCS_FOLDER_NAME)}`
    : undefined;
// Re-export types from hook for parent components
export type {
  EditableBrokerageFields,
  EditableClientFields,
  EditableRfpFields,
  EditedReviewFields,
} from "./hooks/useReviewSectionState";

export interface ReviewSectionProps {
  rfpId: string;
  state: StepState;
  clientDetails?: SendRfpClientDetails;
  brokerageDetails?: SendRfpBrokerageDetails;
  effectiveDate?: string;
  carrierDueDate?: string;
  onBack?: () => void;
  onSubmit: () => void;
  onOpenNotesDrawer: (tab: NotesTab) => void;
  onFieldsChange?: (fields: EditedReviewFields) => void;
  onEditingChange?: (isEditing: boolean) => void;
  rfpNotes?: string;
  coverageNotes?: Record<string, string>;
  coverageNoteSections?: CoverageNoteSection[];
  analystDocsFolderUrl?: string;
  active?: boolean;
  completed?: boolean;
}

interface FieldRowProps {
  label: string;
  value: string;
}

const FieldRow = ({ label, value }: FieldRowProps): React.JSX.Element => (
  <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
    <Typography variant="body-medium">
      <strong>{label}</strong>
    </Typography>
    <Typography variant="body-medium">{value}</Typography>
  </Box>
);

interface RequiredFieldRowProps {
  label: string;
  value: string | undefined;
}

/**
 * Displays a field value, showing "missing" in red if no value
 */
const RequiredFieldRow = ({
  label,
  value,
}: RequiredFieldRowProps): React.JSX.Element => (
  <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
    <Typography variant="body-medium">
      <strong>{label}</strong>
    </Typography>
    {value ? (
      <Typography variant="body-medium">{value}</Typography>
    ) : (
      <Typography variant="body-medium" color="danger">
        missing
      </Typography>
    )}
  </Box>
);

interface EditableFieldRowProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const EditableFieldRow = ({
  label,
  value,
  onChange,
  placeholder,
}: EditableFieldRowProps): React.JSX.Element => (
  <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
    <Typography variant="body-medium">
      <strong>{label}</strong>
    </Typography>
    <TextField
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </Box>
);

/**
 * Formats a date string to RFC3339 format for backend
 */
const formatDateForBackend = (date: string | undefined): string | undefined => {
  if (!date) return undefined;
  return `${date}T00:00:00Z`;
};

const todayDateInputValue = (): string => format(new Date(), "yyyy-MM-dd");

// The stored value is `YYYY-MM-DDT00:00:00Z`, so comparing its date part to the
// user's local today as strings avoids shifting it across the UTC boundary.
export const isCarrierDueDateInPast = (carrierDueDate?: string): boolean =>
  !!carrierDueDate && carrierDueDate.split("T")[0] < todayDateInputValue();

/**
 * Builds client update payload from form fields
 */
const buildClientPayload = (
  fields: EditableClientFields,
): SendRfpClientDetailsUpdate => ({
  name: fields.name,
  numberOfEmployees: parseInt(fields.numberOfEmployees, 10) || 0,
  situsState: fields.situsState,
  hasInternationalEmployees: fields.hasInternationalEmployees === "Yes",
  address: {
    addressLine1: fields.addressStreet,
    addressLine2: fields.addressLine2 || undefined,
    city: fields.addressCity || undefined,
    state: fields.addressState || undefined,
    zipCode: fields.addressZipcode || undefined,
  },
  rfpAdditionalInfo: {
    technologyCreditNeeded: fields.technologyCreditNeeded === "Yes",
    requestedSystem: fields.benAdminSystem || undefined,
    techCreditType:
      fields.technologyCreditNeeded === "Yes"
        ? fields.techCreditType || undefined
        : undefined,
    techCreditAmount:
      fields.technologyCreditNeeded === "Yes" && fields.techCreditAmount
        ? parseFloat(fields.techCreditAmount)
        : undefined,
  },
});

/**
 * Builds brokerage update payload from form fields
 */
interface BrokerageUpdatePayload {
  address: {
    address: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipcode?: string;
  };
  producer: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

const buildBrokeragePayload = (
  fields: EditableBrokerageFields,
): BrokerageUpdatePayload => ({
  address: {
    address: fields.addressStreet,
    addressLine2: fields.addressLine2 || undefined,
    city: fields.addressCity || undefined,
    state: fields.addressState || undefined,
    zipcode: fields.addressZipcode || undefined,
  },
  producer: {
    firstName: fields.producerFirstName,
    lastName: fields.producerLastName,
    email: fields.producerEmail,
  },
});

// ============================================================================
// Sub-Components for MiniSection Content (extracted to reduce complexity)
// ============================================================================

interface RfpDetailsEditContentProps {
  effectiveDate?: string;
  rfpFields: EditableRfpFields;
  analystOptions: AnalystOption[];
  onCarrierDueDateChange: (value: string) => void;
  onAnalystChange: (assignment: AssignedAnalyst) => void;
}

const RfpDetailsEditContent = ({
  effectiveDate,
  rfpFields,
  analystOptions,
  onCarrierDueDateChange,
  onAnalystChange,
}: RfpDetailsEditContentProps): React.JSX.Element => (
  <Box sx={{ display: "flex", gap: 3 }}>
    <FieldRow
      label="Original Eff. Date"
      value={formatEffectiveDate(effectiveDate)}
    />
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Typography variant="body-medium">
        <strong>Carrier Due Date</strong>
      </Typography>
      <TextField
        size="small"
        type="date"
        value={rfpFields.carrierDueDate?.split("T")[0] ?? ""}
        onChange={(e) => onCarrierDueDateChange(e.target.value)}
        error={isCarrierDueDateInPast(rfpFields.carrierDueDate)}
        helperText={
          isCarrierDueDateInPast(rfpFields.carrierDueDate)
            ? "Carrier Due Date must be today or later"
            : undefined
        }
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { min: todayDateInputValue() },
        }}
      />
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Typography variant="body-medium">
        <strong>Assigned Analyst</strong>
      </Typography>
      <FormControl size="small">
        <Select
          value={analystOptionValue(rfpFields.assignedAnalyst)}
          onChange={(e) => {
            const selected = analystOptions.find(
              (option) => option.value === e.target.value,
            );
            onAnalystChange(selected?.assignment ?? NO_ASSIGNED_ANALYST);
          }}
          displayEmpty
        >
          <MenuItem value="">
            <em>Select Analyst</em>
          </MenuItem>
          {analystOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  </Box>
);

interface ClientEditContentProps {
  clientFields: EditableClientFields;
  updateClientField: <K extends keyof EditableClientFields>(
    field: K,
    value: EditableClientFields[K],
  ) => void;
}

const ClientEditContent = ({
  clientFields,
  updateClientField,
}: ClientEditContentProps): React.JSX.Element => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <Box sx={{ display: "flex", gap: 3 }}>
      <EditableFieldRow
        label="Name"
        value={clientFields.name}
        onChange={(value) => updateClientField("name", value)}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          gap: 1,
        }}
      >
        <Typography variant="body-medium">
          <strong>Client Address *</strong>
        </Typography>
        <TextField
          size="small"
          value={clientFields.addressStreet}
          onChange={(e) => updateClientField("addressStreet", e.target.value)}
          placeholder="Street"
          fullWidth
        />
        <TextField
          size="small"
          value={clientFields.addressLine2}
          onChange={(e) => updateClientField("addressLine2", e.target.value)}
          placeholder="Apt/Suite"
          fullWidth
        />
        <TextField
          size="small"
          value={clientFields.addressCity}
          onChange={(e) => updateClientField("addressCity", e.target.value)}
          placeholder="City"
          fullWidth
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            select
            size="small"
            value={clientFields.addressState}
            onChange={(e) => updateClientField("addressState", e.target.value)}
            sx={{ minWidth: 80 }}
          >
            <MenuItem value="">
              <em>State</em>
            </MenuItem>
            {US_STATES.map((state) => (
              <MenuItem key={state} value={state}>
                {state}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            value={clientFields.addressZipcode}
            onChange={(e) =>
              updateClientField("addressZipcode", e.target.value)
            }
            placeholder="Zipcode"
            error={
              !!clientFields.addressZipcode.trim() &&
              !ZIP_CODE_REGEX.test(clientFields.addressZipcode.trim())
            }
            helperText={
              !!clientFields.addressZipcode.trim() &&
              !ZIP_CODE_REGEX.test(clientFields.addressZipcode.trim())
                ? "Zip code must be 5 digits or ZIP+4 format (XXXXX-XXXX)"
                : undefined
            }
            sx={{ flex: 1 }}
          />
        </Box>
      </Box>
    </Box>
    <Box sx={{ display: "flex", gap: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <Typography variant="body-medium">
          <strong>Situs State</strong>
        </Typography>
        <TextField
          select
          size="small"
          value={clientFields.situsState}
          onChange={(e) => updateClientField("situsState", e.target.value)}
        >
          <MenuItem value="">
            <em>Select State</em>
          </MenuItem>
          {US_STATES.map((state) => (
            <MenuItem key={state} value={state}>
              {state}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={{ flex: 1 }} />
    </Box>
    <Box sx={{ display: "flex", gap: 3 }}>
      <EditableFieldRow
        label="Eligible Employees"
        value={clientFields.numberOfEmployees}
        onChange={(value) => updateClientField("numberOfEmployees", value)}
      />
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <Typography variant="body-medium">
          <strong>Intl. Employees</strong>
        </Typography>
        <TextField
          select
          size="small"
          value={clientFields.hasInternationalEmployees}
          onChange={(e) =>
            updateClientField("hasInternationalEmployees", e.target.value)
          }
        >
          {YES_NO_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Box>
    <Box sx={{ display: "flex", gap: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <Typography variant="body-medium">
          <strong>BenAdmin System</strong>
        </Typography>
        <TextField
          select
          size="small"
          value={clientFields.benAdminSystem}
          onChange={(e) => updateClientField("benAdminSystem", e.target.value)}
        >
          <MenuItem value="">
            <em>Select System</em>
          </MenuItem>
          {BEN_ADMIN_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 1 }}>
        <Typography variant="body-medium">
          <strong>Tech Credits</strong>
        </Typography>
        <TextField
          select
          size="small"
          value={clientFields.technologyCreditNeeded}
          onChange={(e) =>
            updateClientField("technologyCreditNeeded", e.target.value)
          }
        >
          {YES_NO_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        {clientFields.technologyCreditNeeded === "Yes" && (
          <>
            <TextField
              select
              size="small"
              value={clientFields.techCreditType}
              onChange={(e) =>
                updateClientField("techCreditType", e.target.value)
              }
            >
              <MenuItem value="">
                <em>Select Type</em>
              </MenuItem>
              {TECH_CREDIT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              value={clientFields.techCreditAmount}
              onChange={(e) =>
                updateClientField("techCreditAmount", e.target.value)
              }
              placeholder="Amount"
              type="number"
            />
          </>
        )}
      </Box>
    </Box>
  </Box>
);

interface ClientDisplayContentProps {
  clientFields: EditableClientFields;
}

const ClientDisplayContent = ({
  clientFields,
}: ClientDisplayContentProps): React.JSX.Element => {
  const address = formatClientAddress(clientFields);
  const hasAddress = Boolean(clientFields.addressStreet.trim());
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 3 }}>
        <RequiredFieldRow
          label="Name *"
          value={clientFields.name || undefined}
        />
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <Typography variant="body-medium">
            <strong>Address *</strong>
          </Typography>
          {hasAddress ? (
            <>
              <Typography variant="body-medium">{address.street}</Typography>
              {address.cityStateZip && (
                <Typography variant="body-medium">
                  {address.cityStateZip}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body-medium" color="danger">
              missing
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 3 }}>
        <RequiredFieldRow
          label="Situs State *"
          value={clientFields.situsState || undefined}
        />
        <Box sx={{ flex: 1 }} />
      </Box>
      <Box sx={{ display: "flex", gap: 3 }}>
        <RequiredFieldRow
          label="Eligible Employees *"
          value={clientFields.numberOfEmployees || undefined}
        />
        <RequiredFieldRow
          label="Intl. Employees *"
          value={clientFields.hasInternationalEmployees || undefined}
        />
      </Box>
      <Box sx={{ display: "flex", gap: 3 }}>
        <FieldRow
          label="BenAdmin System"
          value={
            BEN_ADMIN_OPTIONS.find(
              (o) => o.value === clientFields.benAdminSystem,
            )?.label ||
            clientFields.benAdminSystem ||
            "—"
          }
        />
        <FieldRow
          label="Tech Credits"
          value={clientFields.technologyCreditNeeded || "No"}
        />
      </Box>
      {clientFields.technologyCreditNeeded === "Yes" && (
        <Box sx={{ display: "flex", gap: 3 }}>
          <FieldRow
            label="Tech Credit Type"
            value={
              TECH_CREDIT_OPTIONS.find(
                (o) => o.value === clientFields.techCreditType,
              )?.label ||
              clientFields.techCreditType ||
              "—"
            }
          />
          <FieldRow
            label="Tech Credit Amount"
            value={clientFields.techCreditAmount || "—"}
          />
        </Box>
      )}
    </Box>
  );
};

interface BrokerageEditContentProps {
  brokerageFields: EditableBrokerageFields;
  updateBrokerageField: <K extends keyof EditableBrokerageFields>(
    field: K,
    value: EditableBrokerageFields[K],
  ) => void;
}

const BrokerageEditContent = ({
  brokerageFields,
  updateBrokerageField,
}: BrokerageEditContentProps): React.JSX.Element => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      columnGap: 3,
      rowGap: 2,
    }}
  >
    <FieldRow label="Broker" value={brokerageFields.name || "—"} />
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Typography variant="body-medium">
        <strong>Broker Address *</strong>
      </Typography>
      <TextField
        size="small"
        value={brokerageFields.addressStreet}
        onChange={(e) => updateBrokerageField("addressStreet", e.target.value)}
        placeholder="Street"
        fullWidth
      />
      <TextField
        size="small"
        value={brokerageFields.addressLine2}
        onChange={(e) => updateBrokerageField("addressLine2", e.target.value)}
        placeholder="Apt/Suite"
        fullWidth
      />
      <TextField
        size="small"
        value={brokerageFields.addressCity}
        onChange={(e) => updateBrokerageField("addressCity", e.target.value)}
        placeholder="City"
        fullWidth
      />
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          select
          size="small"
          value={brokerageFields.addressState}
          onChange={(e) => updateBrokerageField("addressState", e.target.value)}
          sx={{ minWidth: 80 }}
        >
          {US_STATES.map((state) => (
            <MenuItem key={state} value={state}>
              {state}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          value={brokerageFields.addressZipcode}
          onChange={(e) =>
            updateBrokerageField("addressZipcode", e.target.value)
          }
          placeholder="Zipcode"
          error={
            !!brokerageFields.addressZipcode.trim() &&
            !ZIP_CODE_REGEX.test(brokerageFields.addressZipcode.trim())
          }
          helperText={
            !!brokerageFields.addressZipcode.trim() &&
            !ZIP_CODE_REGEX.test(brokerageFields.addressZipcode.trim())
              ? "Zip code must be 5 digits or ZIP+4 format (XXXXX-XXXX)"
              : undefined
          }
          sx={{ flex: 1 }}
        />
      </Box>
    </Box>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box>
        <Typography variant="body-medium">
          <strong>Producer First Name</strong>
        </Typography>
        <TextField
          size="small"
          value={brokerageFields.producerFirstName}
          onChange={(e) =>
            updateBrokerageField("producerFirstName", e.target.value)
          }
          fullWidth
        />
      </Box>
      <Box>
        <Typography variant="body-medium">
          <strong>Producer Last Name</strong>
        </Typography>
        <TextField
          size="small"
          value={brokerageFields.producerLastName}
          onChange={(e) =>
            updateBrokerageField("producerLastName", e.target.value)
          }
          fullWidth
        />
      </Box>
      <TextField
        size="small"
        value={brokerageFields.producerEmail}
        onChange={(e) => updateBrokerageField("producerEmail", e.target.value)}
        placeholder="Email"
        fullWidth
      />
    </Box>
  </Box>
);

interface BrokerageDisplayContentProps {
  brokerageFields: EditableBrokerageFields;
}

const BrokerageDisplayContent = ({
  brokerageFields,
}: BrokerageDisplayContentProps): React.JSX.Element => {
  const address = formatBrokerageAddress(brokerageFields);
  const hasAddress = Boolean(brokerageFields.addressStreet.trim());
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        columnGap: 3,
        rowGap: 2,
      }}
    >
      <FieldRow label="Broker" value={brokerageFields.name || "—"} />
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="body-medium">
          <strong>Broker Address *</strong>
        </Typography>
        {hasAddress ? (
          <>
            <Typography variant="body-medium">{address.street}</Typography>
            {address.cityStateZip && (
              <Typography variant="body-medium">
                {address.cityStateZip}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body-medium" color="danger">
            missing
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="body-medium">
          <strong>Producer *</strong>
        </Typography>
        {[
          brokerageFields.producerFirstName,
          brokerageFields.producerLastName,
        ].filter(Boolean).length > 0 ? (
          <>
            <Typography variant="body-medium">
              {[
                brokerageFields.producerFirstName,
                brokerageFields.producerLastName,
              ]
                .filter(Boolean)
                .join(" ")}
            </Typography>
            {brokerageFields.producerEmail && (
              <Typography variant="body-medium">
                {brokerageFields.producerEmail}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body-medium" color="danger">
            missing
          </Typography>
        )}
      </Box>
    </Box>
  );
};

interface NotesContentProps {
  notes: string;
  emptyMessage: string;
}

const NotesContent = ({
  notes,
  emptyMessage,
}: NotesContentProps): React.JSX.Element =>
  notes ? (
    <Typography variant="body-medium" sx={{ whiteSpace: "pre-wrap" }}>
      {notes}
    </Typography>
  ) : (
    <Typography variant="body-medium" color="textSecondary">
      <em>{emptyMessage}</em>
    </Typography>
  );

interface CoverageNotesContentProps {
  coverageNotes: Record<string, string>;
  coverageNoteSections: CoverageNoteSection[];
}

const CoverageNotesContent = ({
  coverageNotes,
  coverageNoteSections,
}: CoverageNotesContentProps): React.JSX.Element => {
  const hasNotes = Object.entries(coverageNotes).some(([, value]) => value);
  if (!hasNotes) {
    return (
      <Typography variant="body-medium" color="textSecondary">
        <em>No coverage notes added</em>
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {coverageNoteSections
        .filter((section) => coverageNotes[section.noteKey])
        .map((section) => (
          <Box key={section.noteKey}>
            <Typography variant="body-medium" color="textSecondary">
              <strong>{section.label}</strong>
            </Typography>
            <Typography variant="body-medium" sx={{ whiteSpace: "pre-wrap" }}>
              {coverageNotes[section.noteKey]}
            </Typography>
          </Box>
        ))}
    </Box>
  );
};

const isClientSaveDisabled = (clientFields: EditableClientFields): boolean => {
  const employeeCount = clientFields.numberOfEmployees.trim();
  const parsedEmployeeCount = /^\d+$/.test(employeeCount)
    ? Number(employeeCount)
    : 0;

  return (
    !clientFields.name.trim() ||
    parsedEmployeeCount <= 0 ||
    !clientFields.situsState ||
    clientFields.situsState.length !== 2 ||
    !clientFields.hasInternationalEmployees ||
    !clientFields.addressStreet.trim() ||
    !clientFields.addressCity.trim() ||
    !clientFields.addressState ||
    clientFields.addressState.length !== 2 ||
    !clientFields.addressZipcode.trim() ||
    !ZIP_CODE_REGEX.test(clientFields.addressZipcode.trim())
  );
};

const isBrokerageSaveDisabled = (
  brokerageFields: EditableBrokerageFields,
): boolean =>
  !brokerageFields.addressStreet.trim() ||
  !brokerageFields.addressCity.trim() ||
  !brokerageFields.addressState ||
  brokerageFields.addressState.length !== 2 ||
  !brokerageFields.addressZipcode.trim() ||
  !ZIP_CODE_REGEX.test(brokerageFields.addressZipcode.trim()) ||
  !brokerageFields.producerFirstName.trim() ||
  !brokerageFields.producerLastName.trim() ||
  !brokerageFields.producerEmail.trim();

const ReviewSection = ({
  rfpId,
  state: _state,
  clientDetails,
  brokerageDetails,
  effectiveDate,
  carrierDueDate,
  onBack,
  onSubmit: _onSubmit,
  onOpenNotesDrawer,
  onFieldsChange,
  onEditingChange,
  rfpNotes = "",
  coverageNotes = {} as Record<string, string>,
  coverageNoteSections = [],
  analystDocsFolderUrl,
  active = false,
  completed = false,
}: ReviewSectionProps): React.JSX.Element => {
  const carrierDocsFolderUrl = buildCarrierDocsFolderUrl(analystDocsFolderUrl);
  // Mutation hooks for saving changes
  const updateClientMutation = useUpdateRfpClient({
    rfpId,
  });

  const updateBrokerageMutation = useUpdateRfpBrokerage({
    rfpId,
  });

  const updateDetailsMutation = useUpdateRfpDetails({
    rfpId,
  });

  const { data: rfpDetails } = useRfpDetails({ rfpId });
  const analystOptions = useMemo(
    () => buildAnalystOptions(rfpDetails),
    [rfpDetails],
  );

  // Use the shared hook for form state management
  const {
    rfpFields,
    clientFields,
    brokerageFields,
    updateRfpField,
    updateClientField,
    updateBrokerageField,
    resetRfpFields,
    resetClientFields,
    resetBrokerageFields,
  } = useReviewSectionState({
    clientDetails,
    brokerageDetails,
    carrierDueDate,
    rfpDetails,
  });

  const notifyUpdates = useCallback((): void => {
    onFieldsChange?.({
      rfp: rfpFields,
      client: clientFields,
      brokerage: brokerageFields,
    });
  }, [onFieldsChange, rfpFields, clientFields, brokerageFields]);

  // Track which sections are in edit mode
  const [editingSections, setEditingSections] = useState<Set<string>>(
    new Set(),
  );

  const handleSectionEditingChange = useCallback(
    (section: string) => (isEditing: boolean) => {
      setEditingSections((prev) => {
        const next = new Set(prev);
        if (isEditing) {
          next.add(section);
        } else {
          next.delete(section);
        }
        return next;
      });
    },
    [],
  );

  const isAnySectionEditing = editingSections.size > 0;

  useEffect(() => {
    onEditingChange?.(isAnySectionEditing);
  }, [isAnySectionEditing, onEditingChange]);

  const handleEditRfpNotes = (): void => {
    onOpenNotesDrawer("rfp");
  };

  const handleEditCoverageNotes = (): void => {
    onOpenNotesDrawer("coverages");
  };

  const clientSaveDisabled = isClientSaveDisabled(clientFields);
  const brokerageSaveDisabled = isBrokerageSaveDisabled(brokerageFields);

  // Save handlers for each section
  const handleSaveRfpDetails = (): void => {
    updateDetailsMutation.mutate(
      {
        carrierDueDate: rfpFields.carrierDueDate,
        ...buildAssignedAnalystPayload(rfpFields.assignedAnalyst),
        // Uneditable fields that still need to be sent in the payload to avoid overwriting existing values
        secondaryAnalystId: rfpDetails?.secondaryAnalyst?.id ?? undefined,
        revisedDueDate: rfpDetails?.revisedDueDate ?? undefined,
        revisedEffectiveDate: rfpDetails?.revisedEffectiveDate ?? undefined,
        inGoodOrderDate: rfpDetails?.inGoodOrderDate ?? undefined,
        estimatedHours: rfpDetails?.estimatedHours ?? undefined,
        totalPlansSpread: rfpDetails?.totalPlansSpread ?? undefined,
        strategicAccountAnalyst:
          rfpDetails?.strategicAccountAnalyst ?? undefined,
        sfaTime: rfpDetails?.sfaTime ?? undefined,
        strategicRfp: rfpDetails?.strategicRfp ?? undefined,
      },
      { onSuccess: notifyUpdates },
    );
  };

  const handleSaveClient = (): void => {
    updateClientMutation.mutate(buildClientPayload(clientFields), {
      onSuccess: notifyUpdates,
    });
  };

  const handleSaveBrokerage = (): void => {
    updateBrokerageMutation.mutate(buildBrokeragePayload(brokerageFields), {
      onSuccess: notifyUpdates,
    });
  };

  const handleAnalystChange = useCallback(
    (assignment: AssignedAnalyst): void => {
      updateRfpField("assignedAnalyst", assignment);
    },
    [updateRfpField],
  );

  return (
    <Section
      title="Review"
      onBack={onBack}
      active={active}
      completed={completed}
      isLast
    >
      <Typography variant="body-medium" color="textSecondary" sx={{ mb: 3 }}>
        Please review all below RFP data for accuracy
      </Typography>

      <MiniSection
        title="RFP Details"
        defaultExpanded
        onSave={handleSaveRfpDetails}
        onCancel={resetRfpFields}
        saveDisabled={isCarrierDueDateInPast(rfpFields.carrierDueDate)}
        onEditingChange={handleSectionEditingChange("rfpDetails")}
        editContent={
          <RfpDetailsEditContent
            effectiveDate={effectiveDate}
            rfpFields={rfpFields}
            analystOptions={analystOptions}
            onCarrierDueDateChange={(value) => {
              const formattedValue = formatDateForBackend(value);
              if (formattedValue) {
                updateRfpField("carrierDueDate", formattedValue);
              }
            }}
            onAnalystChange={handleAnalystChange}
          />
        }
      >
        <Box sx={{ display: "flex", gap: 3 }}>
          <FieldRow
            label="Original Eff. Date"
            value={formatEffectiveDate(effectiveDate)}
          />
          <RequiredFieldRow
            label="Carrier Due Date"
            value={
              rfpFields.carrierDueDate
                ? formatCarrierDueDate(rfpFields.carrierDueDate)
                : undefined
            }
          />
          <RequiredFieldRow
            label="Assigned Analyst"
            value={assignedAnalystName(rfpFields.assignedAnalyst)}
          />
        </Box>
      </MiniSection>

      <MiniSection
        title="Client"
        onSave={handleSaveClient}
        onCancel={resetClientFields}
        onEditingChange={handleSectionEditingChange("client")}
        saveDisabled={clientSaveDisabled}
        editContent={
          <ClientEditContent
            clientFields={clientFields}
            updateClientField={updateClientField}
          />
        }
      >
        <ClientDisplayContent clientFields={clientFields} />
      </MiniSection>

      <MiniSection
        title="Brokerage"
        onSave={handleSaveBrokerage}
        onCancel={resetBrokerageFields}
        onEditingChange={handleSectionEditingChange("brokerage")}
        saveDisabled={brokerageSaveDisabled}
        editContent={
          <BrokerageEditContent
            brokerageFields={brokerageFields}
            updateBrokerageField={updateBrokerageField}
          />
        }
      >
        <BrokerageDisplayContent brokerageFields={brokerageFields} />
      </MiniSection>

      <MiniSection title="RFP Notes" onEdit={handleEditRfpNotes}>
        <NotesContent notes={rfpNotes} emptyMessage="No RFP notes added" />
      </MiniSection>

      <MiniSection title="Coverage Notes" onEdit={handleEditCoverageNotes}>
        <CoverageNotesContent
          coverageNotes={coverageNotes}
          coverageNoteSections={coverageNoteSections}
        />
      </MiniSection>

      <MiniSection
        title="Documents"
        collapsible={false}
        headerAction={
          <Tooltip title="Open SharePoint folder">
            <span>
              <IconButton
                size="small"
                component="a"
                href={carrierDocsFolderUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open SharePoint folder"
                disabled={!carrierDocsFolderUrl}
                sx={{ color: "#7B1FA2" }}
              >
                <SnippetFolderOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
      >
        <Typography variant="body-medium" color="textSecondary">
          Upload supporting documents to the &ldquo;
          {CARRIER_DOCS_FOLDER_NAME}&rdquo; folder in SharePoint. Please remove
          passwords from files before clicking Submit.
        </Typography>
      </MiniSection>
    </Section>
  );
};

export default ReviewSection;
